import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { LeadCloudFileType, VendorCustomerType } from '@econtractor/types';
import type { CloudUploadScope } from 'api/apiCloudflare';
import * as apiVendors from 'api/apiVendors';
import { useToast } from 'context/ToastContext';
import spinnerTitle from 'components/others/spinnerTitle';
import SvgSave from 'components/svg/SvgSave';
import { parseCloudFiles } from 'modules/files';
import {
    CustomerForm,
    EMPTY_CUSTOMER,
    normalizeCustomerPayload,
    parseObjectCustomer,
    useCustomerFormFields,
} from 'modules/objects';

interface OffVendorCustomerEditProps {
    vendorId: number;
    customer: VendorCustomerType | null;
    isCreate: boolean;
    formNonce: number;
    onHide: () => void;
    onSaved: () => Promise<void>;
}

const OffVendorCustomerEdit = ({
    vendorId,
    customer,
    isCreate,
    formNonce,
    onHide,
    onSaved,
}: OffVendorCustomerEditProps) => {
    const { t } = useTranslation(['Vendors', 'Objects', 'common']);
    const { showToast } = useToast();
    const show = isCreate || customer !== null;
    const [savedCustomerId, setSavedCustomerId] = useState(0);
    const [customerFiles, setCustomerFiles] = useState<LeadCloudFileType[]>([]);
    const filesOwnerIdRef = useRef(0);

    useEffect(() => {
        setSavedCustomerId(isCreate ? 0 : Number(customer?.ID || 0));
        setCustomerFiles([]);
        filesOwnerIdRef.current = 0;
    }, [formNonce, isCreate, customer?.ID]);

    const initialCustomer = useMemo(
        () =>
            isCreate
                ? { ...EMPTY_CUSTOMER }
                : parseObjectCustomer({
                      ...customer?.customer,
                      fullName: customer?.fullName,
                      edrpouOrIpn: customer?.edrpouOrIpn,
                      vatEnabled: customer?.vatEnabled ?? customer?.customer?.vatEnabled,
                  }),
        [isCreate, customer],
    );

    const fields = useCustomerFormFields({
        initialCustomer,
        resetKey: `${isCreate ? 'create' : customer?.ID || 0}-${formNonce}`,
        edrpouReadOnly: !isCreate,
    });

    useEffect(() => {
        if (!vendorId || !savedCustomerId) {
            if (!savedCustomerId) setCustomerFiles([]);
            return;
        }
        if (filesOwnerIdRef.current === savedCustomerId) return;

        let cancelled = false;
        void (async () => {
            const row = await apiVendors.getVendorCustomer(vendorId, savedCustomerId);
            if (cancelled) return;
            filesOwnerIdRef.current = savedCustomerId;
            setCustomerFiles(parseCloudFiles(row?.files));
        })();

        return () => {
            cancelled = true;
        };
    }, [vendorId, savedCustomerId, formNonce]);

    const toastSaveError = (errCode?: string) => {
        if (errCode === 'EDRPOU_EXISTS') {
            showToast(t('subjects.messEdrpouExists'), 'warning');
            return;
        }
        if (errCode === 'VENDOR_REQUIRED') {
            showToast(t('subjects.messVendorRequired'), 'warning');
            return;
        }
        if (errCode === 'EDRPOU_REQUIRED') {
            showToast(t('subjects.messCodeRequired'), 'warning');
            return;
        }
        showToast(t('common:messError'), 'danger');
    };

    const persistExisting = async (customerId: number): Promise<boolean> => {
        const payload = normalizeCustomerPayload(fields.formValues);
        fields.setIsSaving(true);
        const result = await apiVendors.updateVendorCustomer(vendorId, customerId, payload);
        fields.setIsSaving(false);

        if (result.ok === false) {
            toastSaveError(result.errCode);
            return false;
        }

        return true;
    };

    const handleCustomerFilesChange = async (nextFiles: LeadCloudFileType[]): Promise<boolean> => {
        setCustomerFiles(nextFiles);
        return true;
    };

    const handleEnsureCustomerForFiles = async (): Promise<CloudUploadScope | false> => {
        const payload = normalizeCustomerPayload(fields.formValues);
        if (!payload.edrpouOrIpn) {
            showToast(t('subjects.messCodeRequired'), 'warning');
            return false;
        }

        if (savedCustomerId) {
            const ok = await persistExisting(savedCustomerId);
            if (!ok) return false;
            filesOwnerIdRef.current = savedCustomerId;
            return { vendorCustomerId: savedCustomerId };
        }

        fields.setIsSaving(true);
        const result = await apiVendors.upsertVendorCustomer(vendorId, payload);
        fields.setIsSaving(false);
        if (result.ok === false) {
            toastSaveError(result.errCode);
            return false;
        }

        const vendorCustomerId = Number(result.id || 0);
        if (!vendorCustomerId) {
            showToast(t('common:messError'), 'danger');
            return false;
        }

        filesOwnerIdRef.current = vendorCustomerId;
        setSavedCustomerId(vendorCustomerId);
        return { vendorCustomerId };
    };

    const finishSave = async (isNew: boolean) => {
        showToast(isNew ? t('subjects.messSaved') : t('subjects.messUpdated'), 'success');
        await onSaved();
        onHide();
    };

    const handleSave = async () => {
        if (fields.isSaving || fields.isYcBusy || fields.isAiBusy) return;

        const payload = normalizeCustomerPayload(fields.formValues);
        if (isCreate && !payload.edrpouOrIpn) {
            showToast(t('subjects.messCodeRequired'), 'warning');
            return;
        }

        const existingId = savedCustomerId || Number(customer?.ID || 0);
        if (existingId) {
            const ok = await persistExisting(existingId);
            if (ok) await finishSave(isCreate);
            return;
        }

        fields.setIsSaving(true);
        const result = await apiVendors.upsertVendorCustomer(vendorId, payload);
        fields.setIsSaving(false);
        if (result.ok === false) {
            toastSaveError(result.errCode);
            return;
        }

        const newId = Number(result.id || 0);
        if (newId) setSavedCustomerId(newId);
        await finishSave(true);
    };

    return (
        <Offcanvas show={show} onHide={onHide} placement="end" className="off-vendor-customer-edit">
            <Offcanvas.Header className="border-bottom" closeButton>
                <Offcanvas.Title>
                    {isCreate ? t('subjects.titleCreate') : t('subjects.titleUpdate')}
                </Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                <div className="off-coming-form d-flex flex-column h-100">
                    <div className="off-coming-content">
                        <CustomerForm
                            formValues={fields.formValues}
                            edrpouReadOnly={fields.edrpouReadOnly}
                            showSaveButton={false}
                            isSaving={fields.isSaving}
                            isYcBusy={fields.isYcBusy}
                            isAiBusy={fields.isAiBusy}
                            ycCodeReady={fields.ycCodeReady}
                            yesLabel={fields.yesLabel}
                            noLabel={fields.noLabel}
                            getPersonTypeLabel={fields.getPersonTypeLabel}
                            handleChange={fields.handleChange}
                            handleFetchYouControl={fields.handleFetchYouControl}
                            refInputFile={fields.refInputFile}
                            uploadProgress={fields.uploadProgress}
                            aiPhase={fields.aiPhase}
                            processingLabel={fields.processingLabel}
                            handleOpenClick={fields.handleOpenClick}
                            handleChangeFile={fields.handleChangeFile}
                            handleCancel={fields.handleCancel}
                            className="px-3 pb-3"
                            customerFiles={customerFiles}
                            vendorCustomerId={savedCustomerId}
                            canEditFiles={true}
                            saveFirstMessage={t('subjects.messSaveCustomerFirst')}
                            onCustomerFilesChange={handleCustomerFilesChange}
                            onEnsureCustomerForFiles={handleEnsureCustomerForFiles}
                        />
                    </div>
                    <div className="p-3 offcanvas-footer off-coming-footer">
                        <div className="d-flex justify-content-end gap-2">
                            <Button
                                variant="info"
                                className="min100px"
                                disabled={fields.isSaving || fields.isAiBusy || fields.isYcBusy}
                                onClick={() => void handleSave()}
                            >
                                {spinnerTitle(
                                    <>
                                        <SvgSave className="me-2 mtop-2" />
                                        {t('common:buttons.save')}
                                    </>,
                                    fields.isSaving,
                                    t('common:buttons.saving'),
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default OffVendorCustomerEdit;
